'use memo'

import { FormikProvider } from 'formik'
import { capitalizeWords } from '@sportninja/common/ts-utils'
import { isCanlan } from '@sportninja/common/utils/customer-name'
import {
  pluginBrandForTenant,
  type PluginKey,
} from '@sportninja/common/features/account/apiAccess'
import { useFullscreen } from 'src/hooks/useFullscreen'
import { WEBSITE_PLUGIN_CARDS } from '../constants'
import { usePluginEmbedForm } from '../hooks/usePluginEmbedForm'
import { ApiAccessCard } from './ApiAccessCard'
import { EmbedCodeSection } from './EmbedCodeSection'
import { PluginBuilderLayout } from './PluginBuilderLayout'
import { PluginPreviewSection } from './PluginPreviewSection'
import { PluginSettingsPanel } from './settings/PluginSettingsPanel'

interface PluginSnippetBuilderProps {
  /** The account's root organization: the Organization view's starting ID. */
  organizationId: string | null
  pluginKey: PluginKey | null
  pluginKeys: PluginKey[]
  onKeyChange: (keyId: string | null) => void
  /** The keys failed to load, so "no key" means unknown, not none. */
  keysFailed: boolean
}

/** Keys are issued per tenant, so the tenant's brand is the key's brand. */
const BRAND = pluginBrandForTenant(isCanlan)

/**
 * The snippet builder: settings on one side, the preview and the embed code
 * they produce on the other. Nothing is saved; the settings live in the code.
 */
export const PluginSnippetBuilder = ({
  organizationId,
  pluginKey,
  pluginKeys,
  onKeyChange,
  keysFailed,
}: PluginSnippetBuilderProps) => {
  const form = usePluginEmbedForm(organizationId)
  const { isFullscreen, toggle: toggleFullscreen } = useFullscreen()

  return (
    <FormikProvider value={form}>
      <PluginBuilderLayout
        isFullscreen={isFullscreen}
        settings={
          <ApiAccessCard
            title={capitalizeWords(WEBSITE_PLUGIN_CARDS.settings.title)}
            subtitle={WEBSITE_PLUGIN_CARDS.settings.subtitle}
            scrollable
          >
            <PluginSettingsPanel brand={BRAND} />
          </ApiAccessCard>
        }
        content={
          <>
            <PluginPreviewSection
              pluginKey={pluginKey}
              pluginKeys={pluginKeys}
              onKeyChange={onKeyChange}
              keysFailed={keysFailed}
              isFullscreen={isFullscreen}
              onToggleFullscreen={toggleFullscreen}
            />
            <EmbedCodeSection pluginKey={pluginKey} keysFailed={keysFailed} />
          </>
        }
      />
    </FormikProvider>
  )
}
